import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Handle,
  Position,
  type Node,
  type Edge,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Activity, Github, Globe, Radio, ShieldAlert, Boxes } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { SystemNodeSummary } from '../../types';

const ICONS: Record<SystemNodeSummary['key'], typeof Activity> = {
  jira: Activity,
  wazuh: ShieldAlert,
  github: Github,
  wordpress: Globe,
  docker: Boxes,
};

const severityRing: Record<SystemNodeSummary['severity'], string> = {
  critical: 'ring-rose-400/70 shadow-rose-500/20',
  warning: 'ring-amber-400/70 shadow-amber-500/20',
  info: 'ring-slate-200 dark:ring-slate-700 shadow-none',
};

const severityDot: Record<SystemNodeSummary['severity'], string> = {
  critical: 'bg-rose-500',
  warning: 'bg-amber-500',
  info: 'bg-slate-300 dark:bg-slate-600',
};

function HubNode() {
  return (
    <div className="flex h-24 w-24 flex-col items-center justify-center rounded-2xl border border-pulse-300 bg-pulse-600 text-white shadow-lg shadow-pulse-500/30">
      <Handle type="source" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Left} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
      <Radio size={22} />
      <p className="mt-1 text-center text-[11px] font-bold leading-tight">Health
        <br />Academy</p>
    </div>
  );
}

function SystemNode({ data }: NodeProps) {
  const node = data as unknown as SystemNodeSummary & { onSelect: (href: string) => void };
  const Icon = ICONS[node.key];
  return (
    <button
      onClick={() => node.onSelect(node.href)}
      className={cn(
        'w-44 rounded-xl border border-slate-200 bg-white p-3 text-left shadow-panel ring-1 transition-transform hover:-translate-y-0.5 dark:border-slate-800 dark:bg-slate-900',
        severityRing[node.severity],
      )}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Right} style={{ opacity: 0 }} />
      <div className="flex items-center gap-2">
        <div
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-lg',
            node.connected ? 'bg-pulse-50 text-pulse-600 dark:bg-pulse-900/30 dark:text-pulse-300' : 'bg-slate-100 text-slate-400 dark:bg-slate-800',
          )}
        >
          <Icon size={14} />
        </div>
        <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{node.label}</p>
        <span className={cn('ml-auto h-2 w-2 rounded-full', severityDot[node.severity])} />
      </div>
      <p className="mt-2 text-xs font-medium text-slate-600 dark:text-slate-300">{node.headline}</p>
      <p className="text-[11px] text-slate-400">{node.detail}</p>
    </button>
  );
}

const nodeTypes = { hub: HubNode, system: SystemNode };

export function SystemMap({ systemNodes }: { systemNodes: SystemNodeSummary[] }) {
  const navigate = useNavigate();

  const { nodes, edges } = useMemo(() => {
    const count = systemNodes.length;
    const radius = 230;
    const centerX = 320;
    const centerY = 220;

    const systemFlowNodes: Node[] = systemNodes.map((s, i) => {
      const angle = (2 * Math.PI * i) / count - Math.PI / 2;
      return {
        id: s.key,
        type: 'system',
        position: { x: centerX + radius * Math.cos(angle) - 88, y: centerY + radius * Math.sin(angle) - 40 },
        data: { ...s, onSelect: (href: string) => navigate(href) },
        draggable: false,
        selectable: false,
      };
    });

    const hubNode: Node = {
      id: 'hub',
      type: 'hub',
      position: { x: centerX - 48, y: centerY - 48 },
      data: {},
      draggable: false,
      selectable: false,
    };

    const flowEdges: Edge[] = systemNodes.map((s) => ({
      id: `hub-${s.key}`,
      source: 'hub',
      target: s.key,
      animated: s.connected,
      style: {
        stroke: s.severity === 'critical' ? '#f43f5e' : s.severity === 'warning' ? '#f59e0b' : '#cbd5e1',
        strokeWidth: 1.5,
      },
    }));

    return { nodes: [hubNode, ...systemFlowNodes], edges: flowEdges };
  }, [systemNodes, navigate]);

  return (
    <div className="h-[500px] w-full overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.3 }}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag={false}
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        preventScrolling={false}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} className="opacity-40" />
      </ReactFlow>
    </div>
  );
}
